import {IServiceFactory} from "../../../service-factory.interface";
import {TimeSpan} from "../../../../types/time-span";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IDialogConfirmationHandler} from "../../../dialog/dialog.service.interface";
import {IDotRezSessionTimerModel} from "./dot-rez-session-timer.interface";
import {DialogCloseButtonBehavior} from "../../../dialog/dialog-enums";
import {
    SessionCountDownDialogComponent
} from "../../../../components/session-timeout/session-count-down-dialog.component";
import React from "react";
import {NullableNumber} from "../../../../types/nullable-types";
import {Check} from "../../../../types/type-checking";
import {isDotRezSessionExpiredError} from "../dot-rez-exception";
import {IDotRezSessionCountDownTimerViewModel} from "./dot-rez-session-timer-view-model.interface";
import {TryRefreshTokenResultEnum} from "./try-refresh-token-result.enum";
import {
    moveBookingSessionDataOnNewToken
} from "../../../booking/booking-strategies/move-booking-session-data-on-new-token";


const COUNT_DOWN_TIMER_VALUE = TimeSpan.fromMinutes(1);


interface DotRezBookingSessionTimerOptions {
    disableCountDownDialog?: boolean;
}

export class DotRezBookingSessionTimerModel implements IDotRezSessionTimerModel, IDotRezSessionCountDownTimerViewModel {
    constructor(token: string,
                tokenExpirationDate: Date,
                private readonly services: IServiceFactory,
                private readonly options?: DotRezBookingSessionTimerOptions) {

        this._token = token;
        this._tokenExpirationDate = tokenExpirationDate;

        makeObservable<this, '_token' | '_tokenExpirationDate' | '_countDownTick'>(this, {
            _token: observable.ref,
            _countDownTick: observable.ref,
            _tokenExpirationDate: observable.ref
        });

        this._startSessionTimer();

        this._reactions.push(reaction(
            () => this.services.navigator.currentRoute,
            () => {
                if (this.services.navigator.routes.home.isActive) {
                    this.dispose();
                }
            }));

        this._reactions.push(reaction(
            () => this.services.application.isActive,
            async (isActive) => {
                if (isActive && !this._isPaused) {
                    if(await this.isExpired()) {
                        this.setExpired(); //in order to show the dialog;
                    } else if(TryRefreshTokenResultEnum.Success !== await this.tryRefreshToken()) {
                        this.setExpired();
                    }

                }
            }));
    }

    private _token: string;
    get token(): string {
        return this._token;
    }

    private _tokenExpirationDate: Date;
    get tokenExpirationDate(): Date {
        return this._tokenExpirationDate;
    }

    private set tokenExpirationDate(value: Date) {
        runInAction(() => {
            this._tokenExpirationDate = value;
        });
    }

    private set token(value: string) {
        moveBookingSessionDataOnNewToken(this._token, value, this.services);
        runInAction(() => {
            this._token = value;
        });



    }

    private _reactions: IReactionDisposer[] = [];
    private _sessionTimerRef: any = null;
    private _countDownTimerRef: any = null;
    private _sessionExpiredDialog: IDialogConfirmationHandler | null = null;
    private _tryRefreshTokenPromise: Promise<TryRefreshTokenResultEnum> = Promise.resolve(TryRefreshTokenResultEnum.Success);

    updateToken(token: string, tokenExpirationDate: Date): void {
        this.token = token;
        this.tokenExpirationDate = tokenExpirationDate
        this._stopCountDownTimer();
        this._stopSessionTimer();
        this._sessionExpiredDialog?.reject();
        this._sessionExpiredDialog = null;
        this._startSessionTimer();

    }

    private _countDownTick: NullableNumber = null;
    public get sessionTimeLeft(): TimeSpan | null {
        if(Check.isNullOrUndefined(this._countDownTick)) {
            return null;
        }

        return TimeSpan.fromMilliseconds(Math.max(0, Math.floor(this.tokenExpirationDate.getTime() - this._countDownTick)));
    }

    private async _waitForRefreshToken(): Promise<void> {
        try {
            await this._tryRefreshTokenPromise;
        } catch (err) {
            this.services.logger.error('_waitForRefreshToken failed', err);
        }

    }

    private _isPaused: boolean = false;
    pause(): void {
        if(this._isPaused) {
            return;
        }
        this._stopCountDownTimer();
        this._stopSessionTimer();
        this._isPaused = true;
    }
    async resume(): Promise<void> {
        if(!this._isPaused) {
            return;
        }

        try {
            const result = await this.tryRefreshToken();
            if(result !== TryRefreshTokenResultEnum.Success) {
                this.setExpired();
            }
        } finally {
            this._isPaused = false;
        }
    }

    async isExpired(): Promise<boolean> {
        if(this._isPaused) {
            return false;
        }
        
        await this._waitForRefreshToken();

        return this.tokenExpirationDate.getTime() <= Date.now();
    }


    setExpired(): void {
        this.tokenExpirationDate = new Date(Date.now() - 1000);
        this._stopSessionTimer();
        this._stopCountDownTimer();
        this._showSessionExpiredDialog();
    }

    private _isDisposed: boolean = false;

    dispose(): void {
        this._disposeReactions();
        this._stopCountDownTimer();
        this._stopSessionTimer();
        this._isDisposed = true;
    }

    private _disposeReactions(): void {
        this._reactions.forEach(r => r());
        this._reactions = [];
    }

    private _stopSessionTimer(): void {
        if(this._sessionTimerRef) {
            clearTimeout(this._sessionTimerRef);
            this._sessionTimerRef = null;
        }
    }

    private async _goHome(): Promise<void> {
        if(this.services.navigator.isOnBookingRoute) {
            this.services.dialog.forceCloseAllDialogs();
            await this.services.navigator.goHome({
                skipRoutingGuards: true
            });
        }
    }

    private async _disposeAndGoToHome(): Promise<void> {
        this.dispose();
        await this._goHome();
    }

    private _showSessionExpiredDialog(): void {
        if(this.options?.disableCountDownDialog) {
            return;
        }


        
        runInAction(() => {
            this._countDownTick = Date.now();
        });

        if(this._sessionExpiredDialog || this._isDisposed) {
            return;
        }

        if(!this.services.navigator.isOnBookingRoute) {
            return;
        }

        const onButtonClick = async () => {
            if(await this.isExpired()) {
                await this._disposeAndGoToHome();
            } else {
                const result = await this.tryRefreshToken();

                if(result !== TryRefreshTokenResultEnum.Success) {
                    if(result === TryRefreshTokenResultEnum.Expired) {
                        this.services.alert.showError(this.services.language.translate('Your session has expired'));
                    } else {
                        this.services.alert.showError(this.services.language.translate('There was an error trying to extend your session'));
                    }
                    await this._disposeAndGoToHome();
                }
            }
        }

        const onCloseDialog = async () => {
            this._sessionExpiredDialog = null;
        }

        this.services.dialog.showPopupDialog({
            closeButtonBehavior: DialogCloseButtonBehavior.None,
            width: '420px',
            render: dialogHandler => {
                this._sessionExpiredDialog = dialogHandler;
                return (<SessionCountDownDialogComponent countDownTimer={this}
                                                         dialogHandler={dialogHandler}
                                                         onButtonClick={onButtonClick}/>);
            },
            onAccept: onCloseDialog,
            onReject: onCloseDialog
        });
    }

    private _stopCountDownTimer(): void {
        if(this._countDownTimerRef) {
            clearInterval(this._countDownTimerRef);
            this._countDownTimerRef = null;
        }
    }

    private _startCountDownTimer(): void {
        this._countDownTimerRef = setInterval(() => {
            runInAction(() => {
                this._countDownTick = Date.now();
            });
        }, 1000);
    }

    private _startSessionTimer(): void {
        const timeUntilExpiration = this.tokenExpirationDate.getTime() - Date.now();
        if(timeUntilExpiration <= COUNT_DOWN_TIMER_VALUE.totalMilliseconds) {
            this._showSessionExpiredDialog();
        } else {
            this._sessionTimerRef = setTimeout(async () => {
                this._startCountDownTimer();
                this._showSessionExpiredDialog();
            }, timeUntilExpiration - COUNT_DOWN_TIMER_VALUE.totalMilliseconds);
        }
    }

    public tryRefreshToken(): Promise<TryRefreshTokenResultEnum> {
        this._tryRefreshTokenPromise = this._tryRefreshToken();
        return this._tryRefreshTokenPromise;
    }

    private async _tryRefreshToken(): Promise<TryRefreshTokenResultEnum> {
        try {

            const {token, dotRezTokenExpirationDate} = await this.services.dotRezApi.tryRefreshToken(this.token);
            this.token = token;
            this.tokenExpirationDate = dotRezTokenExpirationDate;
            this._stopSessionTimer();
            this._stopCountDownTimer();
            this._sessionExpiredDialog?.reject();
            this._sessionExpiredDialog = null;
            this._startSessionTimer();

            return TryRefreshTokenResultEnum.Success;
        } catch (err) {

            const isSessionExpired = isDotRezSessionExpiredError(err);
            if(isSessionExpired) {
                return TryRefreshTokenResultEnum.Expired;
            }

            if(!isSessionExpired) {
                this.services.logger.error('Failed to refresh dotREZ token', err);
            }

            return TryRefreshTokenResultEnum.Failed;
        }
    }
}

